/**
 * Voice channels: the list, who's inside, and the admin mutations.
 *
 * Two queries, deliberately. The list barely ever changes, so it holds a long
 * `staleTime` and is only refetched when someone edits it. Presence changes
 * whenever anybody joins, leaves or starts sharing, and polls at 10s — this is
 * ambient information ("is anyone in there?"), not a notification. Folding it
 * into the list would invalidate the list's structural sharing on every poll,
 * the same trap read receipts avoid in useTeamChat.
 *
 * The backend caches the LiveKit read for 5s, so a room full of pollers stays a
 * trickle of Twirp calls.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createVoiceChannel,
  deleteVoiceChannel,
  fetchVoiceChannels,
  fetchVoicePresence,
  renameVoiceChannel,
  type VoiceParticipant,
} from '../lib/voiceApi';

const CHANNELS_KEY = ['voice-channels'] as const;
const PRESENCE_KEY = ['voice-channels', 'presence'] as const;

/**
 * Prefix match, so this takes presence with it — deliberately. A DELETE kicks
 * everyone out of the room, and leaving stale faces under a channel that no
 * longer exists is the kind of lie that makes people distrust the feature.
 * One extra request on a rare, admin-only action.
 */
function invalidateAll(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: CHANNELS_KEY });
}

export function useVoiceChannels() {
  return useQuery({
    queryKey: CHANNELS_KEY,
    queryFn: fetchVoiceChannels,
    // The list only changes when an admin edits it, and that invalidates.
    staleTime: 5 * 60_000,
  });
}

export function useVoicePresence() {
  return useQuery({
    queryKey: PRESENCE_KEY,
    queryFn: fetchVoicePresence,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  });
}

/** Presence for one channel, with the empty case spelled out for callers. */
export function useVoiceChannelPresence(channelId: string): {
  enabled: boolean;
  participants: VoiceParticipant[];
} {
  const { data } = useVoicePresence();
  return {
    enabled: data?.enabled ?? false,
    participants: data?.channels[channelId] ?? [],
  };
}

export function useCreateVoiceChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createVoiceChannel(name),
    onSettled: () => invalidateAll(qc),
  });
}

export function useRenameVoiceChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameVoiceChannel(id, name),
    onSettled: () => invalidateAll(qc),
  });
}

export function useDeleteVoiceChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteVoiceChannel(id),
    onSettled: () => invalidateAll(qc),
  });
}
